import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Calendar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AssignmentsAdmin() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [courses, setCourses] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ course_id: "", title: "", instructions: "", due_date: "", max_marks: 100, is_mandatory: true });
    const load = async () => {
        const [a, c] = await Promise.all([api.get("/assignments"), api.get("/courses")]);
        setItems(a.data.items);
        setCourses(c.data.items);
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/assignments", form);
            toast.success("Assignment created");
            setOpen(false);
            setForm({ course_id: "", title: "", instructions: "", due_date: "", max_marks: 100, is_mandatory: true });
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="space-y-6" data-testid="admin-assignments-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Manage</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Assignments</h1>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="mx-btn-primary" data-testid="admin-add-assignment-btn"><Plus className="h-4 w-4 mr-2" /> New assignment</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Create assignment</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <div><Label>Course</Label>
                                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                                    <SelectTrigger data-testid="assn-course"><SelectValue placeholder="Select course" /></SelectTrigger>
                                    <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Title</Label><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="assn-title" /></div>
                            <div><Label>Instructions</Label><Textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} rows={4} data-testid="assn-instructions" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Due date</Label><Input type="datetime-local" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
                                <div><Label>Max marks</Label><Input type="number" value={form.max_marks} onChange={e => setForm({ ...form, max_marks: parseInt(e.target.value) || 100 })} /></div>
                            </div>
                            <DialogFooter><Button type="submit" disabled={!form.course_id} className="mx-btn-primary" data-testid="assn-submit">Create</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid gap-4">
                {items.map(a => (
                    <Card key={a.id} className="mx-card" data-testid={`admin-assignment-${a.id}`}>
                        <CardContent className="p-5 flex items-start gap-4">
                            <div className="h-11 w-11 rounded-xl bg-[#F0EFFF] dark:bg-[#1C1A2E] flex items-center justify-center"><FileText className="h-5 w-5 text-[#726AF0]" /></div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{a.course_title}</div>
                                <div className="font-bold text-base mt-1">{a.title}</div>
                                <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{a.instructions}</div>
                                <div className="flex flex-wrap gap-2 mt-2.5">
                                    {a.due_date && <Badge variant="outline" className="text-[10px]"><Calendar className="h-3 w-3 mr-1" /> Due {new Date(a.due_date).toLocaleDateString()}</Badge>}
                                    <Badge variant="outline" className="text-[10px]">Max: {a.max_marks}</Badge>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
                {items.length === 0 && <div className="mx-card p-12 text-center text-muted-foreground">No assignments yet.</div>}
            </div>
        </div>
    );
}
